import { AsyncPipe } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { QuotaAlerts } from '../../models/quota-alerts.model';
import { NgxPermissionsService } from 'ngx-permissions';
import { QuotaService } from '../../services/quota.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
	selector: 'app-utilization-modal',
	imports: [ReactiveFormsModule, AsyncPipe],
	template: `
		<div class="modal-header">
		  <h4 class="modal-title">Richiedi estensione del servizio.</h4>
		  <button type="button" class="btn-close" aria-label="Close" (click)="activeModal.dismiss('Cross Click')"></button>
		</div>
		<div class="modal-body">
		  @if (alerts) {
		    <p>
		      Hai superato la soglia di utilizzo del servizio.
		      <br /><br />
		      La percentuale di utilizzo query è del
		      <span
		        [class.text-warning]="alerts.request.alertType === 'WARNING-REQUEST'"
		        [class.text-danger]="alerts.request.alertType !== null && alerts.request.alertType !== 'WARNING-REQUEST'"
		        [class.text-success]="alerts.request.alertType === null">
		        {{ alerts.request.usagePercent }}%
		      </span>
		      <br />
		      Il tuo spazio in GB utilizzato è del
		      <span
		        [class.text-warning]="alerts.storage.alertType === 'WARNING-STORAGE'"
		        [class.text-danger]="alerts.storage.alertType !== null && alerts.storage.alertType !== 'WARNING-STORAGE'"
		        [class.text-success]="alerts.storage.alertType === null">
		        {{ alerts.storage.usagePercent }}%
		      </span>
		    </p>
		  }
		
		  @if ((hasAdmPermission | async) === true) {
		    <p>
		      Puoi inviare una richiesta di estensione del servizio al tuo Master Admin
		      per richiedere un incremento di query, di spazio o di entrambi.
		    </p>
		
		    <form [formGroup]="motivationForm">
		      <div class="mb-3">
		        <label class="form-label" i18n>Tipologia di richiesta</label>
		        <div class="form-check">
		          <input
		            type="checkbox"
		            id="increaseRequest"
		            class="form-check-input"
		            formControlName="increaseRequest"
		            />
		          <label for="increaseRequest" class="form-check-label" i18n>Aumenta Query</label>
		        </div>
		        <div class="form-check">
		          <input
		            type="checkbox"
		            id="increaseStorage"
		            class="form-check-input"
		            formControlName="increaseStorage"
		            />
		          <label for="increaseStorage" class="form-check-label" i18n>Aumenta GB</label>
		        </div>
		        @if (checkboxError) {
		          <div class="text-danger">
		            <small i18n>Seleziona almeno una opzione.</small>
		          </div>
		        }
		      </div>
		      <textarea
		        id="motivation"
		        formControlName="motivation"
		        class="form-control"
		        rows="4"
		        placeholder="Inserisci un messaggio da mandare al tuo Master Admin per motivare la richiesta."
		      ></textarea>
		      @if (motivationForm.get('motivation')?.invalid && motivationForm.get('motivation')?.touched) {
		        <div class="text-danger">
		          @if (motivationForm.get('motivation')?.errors?.['required']) {
		            <small i18n>La motivazione è obbligatoria.</small>
		          }
		          @if (motivationForm.get('motivation')?.errors?.['minlength']) {
		            <small i18n>La motivazione deve contenere almeno 7 caratteri.</small>
		          }
		        </div>
		      }
		    </form>
		  }
		</div>
		<div class="modal-footer">
		  @if ((hasAdmPermission | async) === true) {
		    <button type="button" class="btn btn-primary"
		      [disabled]="motivationForm.invalid || checkboxError"
		    (click)="onSubmit()" i18n>Conferma</button>
		  }
		  <button type="button" class="btn btn-secondary" (click)="setHiddenTrue()" i18n>Non mostrare più</button>
		</div>
		`
})
export class UtilizationModal {
	private readonly fb = inject(NonNullableFormBuilder);
	private readonly permissionSrv = inject(NgxPermissionsService);
	private readonly quotaSrv = inject(QuotaService);
	private readonly toastSrv = inject(ToastService);

	activeModal = inject(NgbActiveModal);

	@Input() alerts!: QuotaAlerts;

	hasAdmPermission = this.permissionSrv.hasPermission('ROLE_ADM');

	motivationForm: FormGroup = this.fb.group({
		motivation: ['', [Validators.required, Validators.minLength(7)]],
		increaseRequest: [false],
		increaseStorage: [false]
	});

	get checkboxError(): boolean {
		const req = !!this.motivationForm.get('increaseRequest')?.value;
		const stor = !!this.motivationForm.get('increaseStorage')?.value;
		const reqTouched = !!this.motivationForm.get('increaseRequest')?.touched;
		const storTouched = !!this.motivationForm.get('increaseStorage')?.touched;
		return !req && !stor && (reqTouched || storTouched);
	}

	onSubmit(): void {
		if (this.motivationForm.valid && !this.checkboxError) {
			console.log(this.motivationForm.value);
			this.quotaSrv.resourceRequest(
				this.motivationForm.get('increaseRequest')?.value,
				this.motivationForm.get('increaseStorage')?.value,
				this.motivationForm.get('motivation')?.value
			).subscribe({
				next: (response) => {
					this.toastSrv.show({ type: 'success', message: 'Richiesta inviata con successo' });
					this.activeModal.close('Request sent successfully');
				}
			});
		}
	}

	setHiddenTrue() {
		this.hasAdmPermission.then(value => {
			if (value === true) {
				// impost nel localStorage di non mostrare più il modal per 24h
				const hideUntil = new Date();
				hideUntil.setHours(hideUntil.getHours() + 24);
				localStorage.setItem('hideUtilizationModalTo', hideUntil.toISOString());
				this.toastSrv.show({ type: 'info', message: 'L\'alert non verrà più mostrato per le prossime 24 ore.' });
			} else {
				// impost nel localStorage di non mostrare più il modal sino al 25 del mese successivo
				const hideUntil = new Date();
				hideUntil.setMonth(hideUntil.getMonth() + 1);
				hideUntil.setDate(25);
				localStorage.setItem('hideUtilizationModalTo', hideUntil.toISOString());
				this.toastSrv.show({ type: 'info', message: 'L\'alert non verrà più mostrato fino prossimo mese di fatturazione.' });
			}
			this.activeModal.dismiss('Hidden');
		})
	}
}